import { useSnowplowTracking, useTranslation } from '@eti/providers';
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { breakpoints, Button, Heading, Inline, Stack, Text } from '@etg/wings';
import { css } from '@eti/styles';
import { Notification, variants } from '@eti/components';
import { CancelReason, OrderState } from '@eti/schema-types';
import { constants } from '@eti/utils';
import { routes } from '../../../../../constants/routesAndApisConstants';
import type { BoundType } from '../../../models';
import { useScrollToElementViaQueryParams } from '../../../../../common/custom-hooks/scrollToElementViaQueryParams';
import { SHOW_ITINERARY } from '../../../constants/general';
import TitledCard from '../../../../../common/components/TitledCard';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import { isCheckInAvailableToBuy } from '../../../utils';
import TripItineraryDrawer from '../../../../../widgets/trip-itinerary/TripItineraryDrawer';
import BoundCardList from './BoundCardList';

const buttonStyles = css`
  @media (min-width: ${breakpoints._560}) {
    justify-content: flex-end;
  }
`;

const Itinerary = () => {
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const {
    bounds,
    customerDetails,
    journeyType,
    orderNumber,
    orderState,
    refundStatus,
    shouldShowManageTrip,
    isOrderActive,
    cancelReason,
    availableExtraProducts,
  } = useOrderDetails();
  const [isItineraryDrawerOpen, setIsItineraryDrawerOpen] = useState(false);
  useScrollToElementViaQueryParams('itinerary-page-section-heading', SHOW_ITINERARY, () =>
    setIsItineraryDrawerOpen(true),
  );
  const [selectedBoundId, setSelectedBoundId] = useState('');
  const hasFraudNoRefundCancelReason = cancelReason === CancelReason.FraudNoRefund;
  const isCheckInProductAvailableToBuy = isCheckInAvailableToBuy(availableExtraProducts);

  const handleBoundCardSelection = (bound: BoundType) => {
    setSelectedBoundId(bound.boundId);
    setIsItineraryDrawerOpen(true);
  };

  const handleCloseItineraryDrawer = () => {
    setIsItineraryDrawerOpen(false);
  };

  const handleSnowplowOnBoundCard = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Overview',
        action: 'Click',
        label: 'TD/ITINERARY',
      });
    }
  };

  const handleSnowplowOnItineraryButtons = (label: string) => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Overview',
        action: 'Click',
        label,
      });
    }
  };

  const orderStatusNotification = useMemo(() => {
    switch (orderState) {
      case OrderState.Canceled:
        return {
          description: t(
            'MyBookingsWelcomePage.Notification.Error.Description',
            customerDetails.email,
          ),
          title: t('MyBookingsWelcomePage.Notification.Error.Header', orderNumber),
          variant: variants.CRITICAL,
          shouldBeVisible:
            !hasFraudNoRefundCancelReason &&
            (!refundStatus || (refundStatus.cases.length === 0 && !refundStatus.hasChargeback)),
        };
      case OrderState.Changed:
        return {
          description: t(
            'MyBookingsWelcomePage.Notification.Warning.Description',
            customerDetails.email,
          ),
          title: t('MyBookingsWelcomePage.Notification.Warning.Header', orderNumber),
          variant: variants.WARNING,
          shouldBeVisible: true,
        };
      default:
        return null;
    }
  }, [
    customerDetails.email,
    hasFraudNoRefundCancelReason,
    orderNumber,
    orderState,
    refundStatus,
    t,
  ]);

  return (
    <TitledCard
      data-testid="itinerary-page-section"
      headingId="itinerary-page-section-heading"
      title={t('OrderDetails.Itinerary.Overview.Header')}
    >
      <Stack>
        {orderStatusNotification?.shouldBeVisible && (
          <Notification variant={orderStatusNotification?.variant}>
            <Stack className={constants.MOUSE_FLOW_CLASS_NAMES} spacing={4}>
              <Heading as="h2" level={4}>
                {orderStatusNotification?.title}
              </Heading>
              <Text>{orderStatusNotification?.description}</Text>
            </Stack>
          </Notification>
        )}
        <Stack spacing={24}>
          <BoundCardList
            ariaControls="itinerary-drawer"
            ariaExpanded={isItineraryDrawerOpen ? 'true' : 'false'}
            ariaHaspopup="dialog"
            handleBoundCardSelection={(bound) => {
              handleBoundCardSelection(bound);
              handleSnowplowOnBoundCard();
            }}
          />
          <Inline className={buttonStyles} collapseBelow={breakpoints._560}>
            {shouldShowManageTrip && (
              <Button
                as={Link}
                data-testid="btnChangeTrip"
                onClick={() => {
                  handleSnowplowOnItineraryButtons('MANAGE_TRIP_CTA');
                }}
                to={routes.ORDER_DETAILS_MANAGE_TRIP}
                variant={isCheckInProductAvailableToBuy ? 'secondary' : 'primary'}
              >
                {t('OrderDetails.Tab.ManageTrip.Description')}
              </Button>
            )}
            {isCheckInProductAvailableToBuy && isOrderActive && (
              <Button
                as={Link}
                data-testid="check-in-button"
                onClick={() => {
                  handleSnowplowOnItineraryButtons('CHECK_IN_CTA');
                }}
                to={routes.ORDER_DETAILS_CHECK_IN}
                variant="primary"
              >
                {t('OrderDetails.Tab.CheckIn.Description')}
              </Button>
            )}
          </Inline>
        </Stack>
        <TripItineraryDrawer
          bounds={bounds}
          id="itinerary-drawer"
          isOpen={isItineraryDrawerOpen}
          onClose={handleCloseItineraryDrawer}
          selectedBoundId={selectedBoundId}
          title={t('IbeClient.TripInfo.Header')}
          tripType={journeyType}
        />
      </Stack>
    </TitledCard>
  );
};

export default Itinerary;
